import { z } from "zod";
import type { BusinessRole } from "./enums.ts";

/**
 * Support tickets (strategy repo: teamna/strategy/support-system.md). The words and rules that Desk
 * web, mobile, the API and Control Center all read. Pure: no I/O.
 */

/**
 * The four fixed kinds behind the team's own board columns. The kind decides what the sender sees;
 * the column name is for the Teamna team only.
 */
export const supportKinds = ["open", "fix_pending", "waiting_customer", "closed"] as const;
export const supportKindSchema = z.enum(supportKinds);
export type SupportKind = z.infer<typeof supportKindSchema>;

/** How blocking the problem is, chosen by the sender. */
export const supportUrgencies = ["low", "normal", "blocking"] as const;
export const supportUrgencySchema = z.enum(supportUrgencies);
export type SupportUrgency = z.infer<typeof supportUrgencySchema>;

/** The team's own priority. The sender never sees it. */
export const supportPriorities = ["none", "low", "medium", "high", "urgent"] as const;
export const supportPrioritySchema = z.enum(supportPriorities);
export type SupportPriority = z.infer<typeof supportPrioritySchema>;

/** The colours a column or a tag can have. Each maps to a theme token in Control Center. */
export const supportColors = ["slate", "blue", "teal", "green", "gold", "red", "purple"] as const;
export const supportColorSchema = z.enum(supportColors);
export type SupportColor = z.infer<typeof supportColorSchema>;

export const supportAuthorKinds = ["customer", "team", "system"] as const;
export type SupportAuthorKind = (typeof supportAuthorKinds)[number];

export const supportRatings = ["up", "down"] as const;
export const supportRatingSchema = z.enum(supportRatings);
export type SupportRating = z.infer<typeof supportRatingSchema>;

/** The files a ticket can carry: screenshots and PDFs. */
export const supportFileTypes = ["image/png", "image/jpeg", "image/webp", "application/pdf"] as const;
export const supportFileTypeSchema = z.enum(supportFileTypes);
export type SupportFileType = z.infer<typeof supportFileTypeSchema>;

export const SUPPORT_LIMITS = {
  subject: 120,
  body: 5_000,
  filesPerMessage: 5,
  fileBytes: 10 * 1024 * 1024,
  fileName: 120,
  /** New tickets one person can send in one hour. */
  ticketsPerHour: 10,
  /** Replies one person can send on one ticket in one hour. */
  repliesPerHour: 30,
  /** A ticket that waits on the customer this long with no answer closes by itself. */
  autoCloseDays: 7,
  /** A label name in Control Center. */
  tagName: 40,
  /** A board column name in Control Center. */
  statusName: 40,
  /** Board columns that are not archived. */
  statuses: 20,
  /** The title of a saved reply in Control Center. */
  savedReplyTitle: 80,
  /** Rows one search in the Control Center list returns, newest activity first. */
  listRows: 500,
} as const;

/** Who can send a ticket: every team role (the front desk too, T1). Members and participants cannot in V1 (spec section 2). */
export function canSendSupport(role: BusinessRole | null): role is BusinessRole {
  return role === "owner" || role === "admin" || role === "staff" || role === "frontdesk";
}

/** The owner and the admins see every ticket of the business; staff and the front desk see their own. */
export function seesAllSupportTickets(role: BusinessRole | null): boolean {
  return role === "owner" || role === "admin";
}

/** The words the sender reads for each kind (spec section 5). Never the column name. */
export const supportStateWords: Record<SupportKind, string> = {
  open: "We're looking at it",
  fix_pending: "We're working on a fix",
  waiting_customer: "Waiting for your reply",
  closed: "Resolved",
};

/** The urgency choices, as the sender reads them. */
export const supportUrgencyWords: Record<SupportUrgency, string> = {
  low: "Just a question",
  normal: "It slows me down",
  blocking: "I can't run my sessions",
};

export const supportPriorityWords: Record<SupportPriority, string> = {
  none: "No priority",
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

/** The words a saved reply can hold, filled when a team member puts it in the composer. */
export const savedReplyFields = ["{first_name}", "{business_name}"] as const;

/**
 * A saved reply with its fields filled: `{first_name}` is the first name of the sender, and
 * `{business_name}` the name of the business. An unknown name leaves a neutral word.
 */
export function fillSavedReply(body: string, values: { senderName: string | null; businessName: string }): string {
  const first = values.senderName?.trim().split(/\s+/)[0] || "there";
  return body.replaceAll("{first_name}", first).replaceAll("{business_name}", values.businessName);
}

/** The ticket number as people read it. */
export function supportTicketLabel(number: number): string {
  return `#${number}`;
}

/**
 * What the app adds by itself, shown only in Control Center. Every value is short text the app
 * knows, never something the person typed.
 */
export const supportContextSchema = z
  .object({
    platform: z.enum(["web", "ios", "android"]).optional(),
    appVersion: z.string().max(40).optional(),
    page: z.string().max(200).optional(),
    device: z.string().max(200).optional(),
  })
  .strict();
export type SupportContext = z.infer<typeof supportContextSchema> & { role?: BusinessRole; timezone?: string };
